import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Form } from 'react-bootstrap';
import RPNInput, { isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { getPhoneCountry, resolvePhoneCountry } from '../../utils/phone';
import 'react-phone-number-input/style.css';

/**
 * Country-aware phone field, built on react-phone-number-input (which uses the
 * same libphonenumber-js metadata as our own validation, so the widget and
 * utils/phone.js can never disagree).
 *
 * The library owns the country dropdown, the flag, and as-you-type national
 * formatting; we keep the label, the invalid state, and native form validation
 * so an invalid number blocks submit the way `required` does.
 *
 * Props (unchanged from the hand-rolled version this replaces)
 *   value            stored string — E.164 ("+40721172247"). Legacy national
 *                    values ("0721172247") are upgraded on load.
 *   onChange(value)  E.164 while the number is parseable, '' when cleared
 *   onValidityChange(bool)  optional; fires whenever validity changes
 *   defaultCountry   ISO ("RO") or legacy dial digits ("40"); defaults to the
 *                    company setting
 *   label, required, name, placeholder, className, disabled, id
 *
 * Flags are bundled locally (react-phone-number-input/flags) rather than the
 * library's default CDN URLs — no external request, works offline.
 */

// Regional-indicator flag emoji from an ISO alpha-2 code.
export const flagFor = (iso) => String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

let cachedOptions = null;
/** Every country libphonenumber knows: { iso, name, dial, flag }, sorted by name. */
export const getPhoneCountryOptions = () => {
    if (cachedOptions) return cachedOptions;
    let names = null;
    try {
        names = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;
    } catch (e) {
        names = null;
    }
    const nameOf = (iso) => {
        try { return (names && names.of(iso)) || iso; } catch (e) { return iso; }
    };
    cachedOptions = getCountries()
        .map((iso) => ({ iso, name: nameOf(iso), dial: getCountryCallingCode(iso), flag: flagFor(iso) }))
        .sort((a, b) => a.name.localeCompare(b.name));
    return cachedOptions;
};

/* The library ships an unstyled skeleton; dress it to match the app's inputs. */
const styles = `
  .sbr-phone .PhoneInput{display:flex;align-items:stretch;gap:8px}
  .sbr-phone .PhoneInputCountry{display:flex;align-items:center;gap:6px;padding:0 12px;margin:0;border:1px solid var(--line,#dee2e6);border-radius:10px;background:var(--surface,#f8fafc);cursor:pointer;position:relative}
  .sbr-phone .PhoneInputCountryIcon{width:26px;height:19px;box-shadow:none;background:transparent;border-radius:3px;overflow:hidden;display:flex}
  .sbr-phone .PhoneInputCountryIcon img{width:100%;height:100%;object-fit:cover;display:block}
  .sbr-phone .PhoneInputCountryIcon--border{box-shadow:none;background:transparent}
  .sbr-phone .PhoneInputCountrySelectArrow{width:6px;height:6px;margin-left:2px;opacity:.5;border-style:solid;border-width:0 1.5px 1.5px 0;transform:rotate(45deg);margin-top:-3px}
  .sbr-phone .PhoneInputCountrySelect{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;border:0}
  .sbr-phone .PhoneInputInput{flex:1;min-width:0;border:1px solid var(--line,#dee2e6);border-radius:10px;padding:9px 12px;font:600 14px inherit;color:var(--ink,#111827);background:#fff}
  .sbr-phone .PhoneInputInput:focus{outline:0;border-color:var(--brand,#2563eb);box-shadow:0 0 0 3px rgba(37,99,235,.12)}
  .sbr-phone .PhoneInputInput::placeholder{font-weight:500;color:var(--faint,#9ca3af)}
  .sbr-phone.is-invalid .PhoneInputInput{border-color:var(--bad,#dc2626)}
  .sbr-phone .PhoneInput--disabled .PhoneInputInput,.sbr-phone .PhoneInput--disabled .PhoneInputCountry{opacity:.6;cursor:default}
`;

/** Stored values predate this widget: upgrade a national number to E.164 so
 *  the library can display it, and leave anything unparseable alone. */
const toE164 = (raw, country) => {
    const text = String(raw ?? '').trim();
    if (!text) return undefined;
    const parsed = parsePhoneNumberFromString(text, country);
    if (parsed && parsed.number) return parsed.number;
    return text.startsWith('+') ? text : undefined;
};

const PhoneInput = ({
    value,
    onChange,
    onValidityChange,
    label,
    required,
    name,
    placeholder,
    className,
    defaultCountry,
    disabled,
    id,
}) => {
    const { t } = useTranslation();
    const fallbackCountry = resolvePhoneCountry(defaultCountry) || getPhoneCountry();

    // The library is the source of truth while the user types (it holds the
    // partial text). We mirror what we last handed the parent so a re-render
    // with our own value does not wipe a half-typed number.
    const [val, setVal] = useState(() => toE164(value, fallbackCountry));
    const lastEmitted = useRef(value ?? '');
    const inputRef = useRef(null);

    // Re-derive only when the parent hands us something we did not emit
    // (loading a record, form reset).
    useEffect(() => {
        const next = value ?? '';
        if (next === lastEmitted.current) return;
        lastEmitted.current = next;
        setVal(toE164(next, fallbackCountry));
    }, [value, fallbackCountry]);

    const isEmpty = !val;
    const isValid = useMemo(() => !!val && isValidPhoneNumber(val), [val]);
    const showInvalid = !isEmpty && !isValid;

    // Native form validation: an invalid number blocks submit with a message,
    // without the parent having to wire anything.
    useEffect(() => {
        if (inputRef.current) inputRef.current.setCustomValidity(showInvalid ? i18next.t('phoneInput.invalidNumber', 'Enter a valid number') : '');
    }, [showInvalid]);

    // Latest callback in a ref so an inline arrow from the parent does not
    // re-fire the effect on every render.
    const validityCb = useRef(onValidityChange);
    validityCb.current = onValidityChange;
    useEffect(() => {
        if (validityCb.current) validityCb.current(isEmpty ? !required : isValid);
    }, [isEmpty, isValid, required]);

    const handleChange = (next) => {
        setVal(next);
        const out = next || '';
        lastEmitted.current = out;
        onChange(out);
    };

    return (
        <Form.Group className={className}>
            <style>{styles}</style>
            {label && <Form.Label htmlFor={id}>{label} {required && <span className="text-danger">*</span>}</Form.Label>}
            <div className={`sbr-phone${showInvalid ? ' is-invalid' : ''}`}>
                <RPNInput
                    ref={inputRef}
                    id={id}
                    name={name}
                    international
                    flags={flags}
                    defaultCountry={fallbackCountry}
                    countryCallingCodeEditable={false}
                    value={val}
                    onChange={handleChange}
                    required={required}
                    disabled={disabled}
                    autoComplete="tel"
                    placeholder={placeholder || t('phoneInput.localNumberPlaceholder')}
                    countrySelectProps={{ 'aria-label': t('phoneInput.phoneCountry') }}
                />
            </div>
            {showInvalid && <Form.Text className="text-danger d-block">{t('phoneInput.invalidNumber', 'Enter a valid number')}</Form.Text>}
        </Form.Group>
    );
};

export default PhoneInput;
